import axios from 'axios';
import {doRequest} from '../../helpers/network';
import {REQUEST_TYPE} from '../../constants';
import {bfa} from '../../helpers/Urls';
import UseAbortApiCall from '../../Common/Hooks/use-abort';

// BFA Mapping action types
export const FETCH_BFA_MAPPING_DATA = 'FETCH_BFA_MAPPING_DATA';
export const FETCH_BFA_MAPPING_DATA_COMPLETED =
  'FETCH_BFA_MAPPING_DATA_COMPLETED';
export const FETCH_BFA_MAPPING_DATA_FAILED = 'FETCH_BFA_MAPPING_DATA_FAILED';

export const SAVE_BFA_MAPPING = 'SAVE_BFA_MAPPING';
export const SAVE_BFA_MAPPING_COMPLETED = 'SAVE_BFA_MAPPING_COMPLETED';
export const SAVE_BFA_MAPPING_FAILED = 'SAVE_BFA_MAPPING_FAILED';

const TOGGLE_BFA_MAPPING_ADD_FORM = 'TOGGLE_BFA_MAPPING_ADD_FORM';
const SET_BFA_MAPPING_SEARCH = 'SET_BFA_MAPPING_SEARCH';

// Action types
const FETCH_REVENUE_PENDING_ORDERS = 'FETCH_REVENUE_PENDING_ORDERS';
const FETCH_REVENUE_PENDING_ORDERS_COMPLETED =
  'FETCH_REVENUE_PENDING_ORDERS_COMPLETED';
const FETCH_REVENUE_PENDING_ORDERS_FAILED =
  'FETCH_REVENUE_PENDING_ORDERS_FAILED';

const FETCH_DELTA_UPDATED_ORDERS = 'FETCH_DELTA_UPDATED_ORDERS';
const FETCH_DELTA_UPDATED_ORDERS_COMPLETED =
  'FETCH_DELTA_UPDATED_ORDERS_COMPLETED';
const FETCH_DELTA_UPDATED_ORDERS_FAILED = 'FETCH_DELTA_UPDATED_ORDERS_FAILED';

const SAVE_DELTA_ORDER = 'SAVE_DELTA_ORDER';
const SAVE_DELTA_ORDER_COMPLETED = 'SAVE_DELTA_ORDER_COMPLETED';
const SAVE_DELTA_ORDER_FAILED = 'SAVE_DELTA_ORDER_FAILED';

const ADD_DELTA_ORDER_REMARK = 'ADD_DELTA_ORDER_REMARK';
const ADD_DELTA_ORDER_REMARK_COMPLETED = 'ADD_DELTA_ORDER_REMARK_COMPLETED';
const ADD_DELTA_ORDER_REMARK_FAILED = 'ADD_DELTA_ORDER_REMARK_FAILED';

const BFA_FILTER = 'BFA_FILTER';
const CLEAR_BFA_FILTER = 'CLEAR_BFA_FILTER';

export const FETCH_SOURCE_DESTINATION_DROPDOWNS =
  'FETCH_SOURCE_DESTINATION_DROPDOWNS';
export const FETCH_SOURCE_DESTINATION_DROPDOWNS_COMPLETED =
  'FETCH_SOURCE_DESTINATION_DROPDOWNS_COMPLETED';
export const FETCH_SOURCE_DESTINATION_DROPDOWNS_FAILED =
  'FETCH_SOURCE_DESTINATION_DROPDOWNS_FAILED';

const fetchOrders = ({
  startType,
  successType,
  failureType,
  tab,
  page,
  search,
}) => async (dispatch) => {
  try {
    dispatch({type: startType});

    const response = await doRequest({
      method: REQUEST_TYPE.GET,
      url: bfa.getDeltaRevenuePendingOrdersURL(tab, page, search),
    });

    const {data, status} = response || {};
    if (status) {
      dispatch({type: successType, payload: data});
      return {success: true, data};
    } else {
      dispatch({
        type: failureType,
        payload: {
          error: response?.message || `Failed to fetch ${tab} orders`,
        },
      });
      return {
        success: false,
        message: response?.message || `Failed to fetch ${tab} orders`,
      };
    }
  } catch (err) {
    dispatch({
      type: failureType,
      payload: {error: err.message},
    });
    return {
      success: false,
      message: err.message || 'Unexpected error occurred',
    };
  }
};

// Fetch Revenue Pending Orders
export const fetchRevenuePendingOrders = (
  tab = 'pending',
  page = 1,
  search = '',
) => {
  return fetchOrders({
    startType: FETCH_REVENUE_PENDING_ORDERS,
    successType: FETCH_REVENUE_PENDING_ORDERS_COMPLETED,
    failureType: FETCH_REVENUE_PENDING_ORDERS_FAILED,
    tab,
    page,
    search,
  });
};

// Fetch Revenue Updated Orders for Delta
export const fetchDeltaUpdatedOrders = (
  tab = 'updated',
  page = 1,
  search = '',
) => {
  return fetchOrders({
    startType: FETCH_DELTA_UPDATED_ORDERS,
    successType: FETCH_DELTA_UPDATED_ORDERS_COMPLETED,
    failureType: FETCH_DELTA_UPDATED_ORDERS_FAILED,
    tab,
    page,
    search,
  });
};

export const applyBFAFilter = (filter = {}, tripStatus = '') => {
  return (dispatch) => {
    dispatch({
      type: BFA_FILTER,
      payload: filter,
    });
  };
};

export const clearBfaFilter = (filter = {}) => {
  return (dispatch) => {
    dispatch({
      type: CLEAR_BFA_FILTER,
    });
  };
};

// Save Delta Order
export const saveDeltaOrder = (orderId, updatedData) => {
  return async (dispatch) => {
    try {
      dispatch({type: SAVE_DELTA_ORDER, payload: {orderId}});

      const response = await doRequest({
        method:
          updatedData?.tab === 'updated' ? REQUEST_TYPE.PUT : REQUEST_TYPE.POST,
        url:
          updatedData?.tab === 'updated'
            ? bfa.saveUpdatedDeltaOrderURL()
            : bfa.saveDeltaOrderURL(), // Create in URLs helper
        data: updatedData,
      });

      const {data, status} = response || {};
      if (status) {
        dispatch({
          type: SAVE_DELTA_ORDER_COMPLETED,
          payload: {orderId, data, tab: updatedData?.tab},
        });
        return {success: true, data};
      } else {
        dispatch({
          type: SAVE_DELTA_ORDER_FAILED,
          payload: {error: response?.message || 'Failed to save delta order'},
        });
        return {
          success: false,
          message: response?.message || 'Failed to save delta order',
        };
      }
    } catch (err) {
      dispatch({
        type: SAVE_DELTA_ORDER_FAILED,
        payload: {error: err.message},
      });
      return {
        success: false,
        message: err.message || 'Unexpected error occurred',
      };
    }
  };
};

// Add Delta Order Remark
export const addDeltaOrderRemark = (orderId, remark) => {
  return async (dispatch) => {
    try {
      dispatch({type: ADD_DELTA_ORDER_REMARK, payload: {orderId}});

      const response = await doRequest({
        method: REQUEST_TYPE.POST,
        url: bfa.addDeltaOrderRemarkURL(), // 👈 create in URLs helper
        data: {id: orderId, remarks: remark},
      });

      const {data, status} = response || {};
      if (status) {
        dispatch({
          type: ADD_DELTA_ORDER_REMARK_COMPLETED,
          payload: {orderId, remark, data},
        });
        return {success: true, data};
      } else {
        dispatch({
          type: ADD_DELTA_ORDER_REMARK_FAILED,
          payload: {error: response?.message || 'Failed to add remark'},
        });
        return {
          success: false,
          message: response?.message || 'Failed to add remark',
        };
      }
    } catch (err) {
      dispatch({
        type: ADD_DELTA_ORDER_REMARK_FAILED,
        payload: {error: err.message},
      });
      return {
        success: false,
        message: err.message || 'Unexpected error occurred',
      };
    }
  };
};

export const getSourceDestinationDropdowns = (customer_ids) => {
  return async (dispatch) => {
    let response = {};
    try {
      response = await doRequest({
        method: REQUEST_TYPE.GET,
        url: bfa.getSourceDestinationDropdownsURL(customer_ids),
      });

      const {data, status} = response || {};
      if (status) {
        dispatch({
          type: FETCH_SOURCE_DESTINATION_DROPDOWNS_COMPLETED,
          payload: data,
        });
      } else {
        dispatch({
          type: FETCH_SOURCE_DESTINATION_DROPDOWNS_FAILED,
          payload: {error: response?.message || 'Failed to fetch dropdowns'},
        });
      }
    } catch (err) {
      dispatch({
        type: FETCH_SOURCE_DESTINATION_DROPDOWNS_FAILED,
        payload: {error: err.message},
      });
    }
    return response;
  };
};

export const toggleBFAMappingAddForm = () => (dispatch) => {
  dispatch({type: TOGGLE_BFA_MAPPING_ADD_FORM});
};

export const setBFAMappingSearch = (search = '') => (dispatch) => {
  dispatch({type: SET_BFA_MAPPING_SEARCH, payload: search});
};

export const fetchBFAMappingData = ({page = 1, search = ''} = {}) => {
  return async (dispatch) => {
    let response = {};
    let {cancelApiCall} = UseAbortApiCall(
      'cancelPreviousRequestFetchBFAMapping',
    );
    try {
      dispatch({type: FETCH_BFA_MAPPING_DATA});
      response = await doRequest({
        method: REQUEST_TYPE.GET,
        cancelToken: cancelApiCall(),
        url: bfa.fetchBFAMappingURL(page, search),
      });
      const {data, status} = response || {};
      if (status) {
        dispatch({type: FETCH_BFA_MAPPING_DATA_COMPLETED, payload: data});
      } else {
        const {message = 'Error'} = response || {};
        dispatch({
          type: FETCH_BFA_MAPPING_DATA_FAILED,
          payload: {error: message},
        });
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        return {error: true};
      }
      dispatch({
        type: FETCH_BFA_MAPPING_DATA_FAILED,
        payload: {error: err.message},
      });
    }
    return response;
  };
};

export const saveBFAMapping = (data, id) => {
  return async (dispatch) => {
    let response = {};
    try {
      dispatch({type: SAVE_BFA_MAPPING});
      response = await doRequest({
        method: id ? REQUEST_TYPE.PUT : REQUEST_TYPE.POST,
        url: bfa.saveBFAMappingURL(),
        data,
      });
      const {data: responseData, status} = response || {};
      if (status) {
        dispatch({
          type: SAVE_BFA_MAPPING_COMPLETED,
          payload: {
            data: responseData,
            formData: data,
            isEdit: !!id,
            mappingObjId: id,
          },
        });
      } else {
        const {message = 'Error'} = response || {};
        dispatch({
          type: SAVE_BFA_MAPPING_FAILED,
          payload: {error: message},
        });
      }
    } catch (err) {
      dispatch({
        type: SAVE_BFA_MAPPING_FAILED,
        payload: {error: err.message},
      });
    }
    return response;
  };
};

// Initial state
const INITIAL_STATE = {
  revenuePendingOrders: [],
  revenueUpdatedOrders: [],
  tabCount: {},
  loadingPending: false,
  loadingRevenueUpdatedOrders: false,
  fetchPendingError: null,
  fetchDeltaUpdatedError: null,
  savingOrder: false,
  saveError: null,
  addingRemark: false,
  addRemarkError: null,
  filters: null,
  bfaFilter: '',
  isFetchingBFAMapping: false,
  bfaMappingData: {},
  isSavingBFAMapping: false,
  showBFAMappingAddForm: false,
  bfaMappingSearch: '',
};

// Reducer
export default (state = INITIAL_STATE, action) => {
  const {type, payload} = action;

  switch (type) {
    // Pending Orders
    case FETCH_REVENUE_PENDING_ORDERS:
      return {...state, loadingPending: true, fetchPendingError: null};
    case FETCH_REVENUE_PENDING_ORDERS_COMPLETED:
      return {
        ...state,
        loadingPending: false,
        revenuePendingOrders: payload,
        tabCount: payload?.revenue_count_dict,
        filters: payload?.filters,
        responseData: payload,
      };
    case FETCH_REVENUE_PENDING_ORDERS_FAILED:
      return {
        ...state,
        loadingPending: false,
        fetchPendingError: payload?.error,
      };

    // Delta Updated Orders
    case FETCH_DELTA_UPDATED_ORDERS:
      return {
        ...state,
        loadingRevenueUpdatedOrders: true,
        fetchDeltaUpdatedError: null,
      };
    case FETCH_DELTA_UPDATED_ORDERS_COMPLETED:
      return {
        ...state,
        loadingRevenueUpdatedOrders: false,
        revenueUpdatedOrders: payload,
        tabCount: payload?.revenue_count_dict,
        filters: payload?.filters,
        responseData: payload,
      };
    case FETCH_DELTA_UPDATED_ORDERS_FAILED:
      return {
        ...state,
        loadingRevenueUpdatedOrders: false,
        fetchDeltaUpdatedError: payload?.error,
      };

    case BFA_FILTER:
      return {
        ...state,
        bfaFilter: payload,
      };

    case CLEAR_BFA_FILTER:
      return {
        ...state,
        bfaFilter: '',
      };

    // Save Order
    case SAVE_DELTA_ORDER:
      return {...state, savingOrder: true, saveError: null};
    case SAVE_DELTA_ORDER_COMPLETED: {
      if (payload.tab === 'updated') {
        return {
          ...state,
          savingOrder: false,
        };
      }
      return {
        ...state,
        savingOrder: false,
        revenuePendingOrders: {
          ...state.revenuePendingOrders,
          revenue_pending: state?.revenuePendingOrders?.revenue_pending?.filter(
            (order) => order.order_id !== payload.orderId,
          ),
        },
        tabCount: {
          ...state.tabCount,
          revenue_pending_count: Math.max(
            (state.tabCount?.revenue_pending_count || 0) - 1,
            0,
          ),
          revenue_updated_count:
            (state.tabCount?.revenue_updated_count || 0) + 1,
        },
      };
    }
    case SAVE_DELTA_ORDER_FAILED:
      return {...state, savingOrder: false, saveError: payload?.error};

    // Add Remark
    case ADD_DELTA_ORDER_REMARK:
      return {...state, addingRemark: true, addRemarkError: null};

    case ADD_DELTA_ORDER_REMARK_COMPLETED:
      return {
        ...state,
        addingRemark: false,
        revenuePendingOrders: {
          ...state.revenuePendingOrders,
          revenue_pending: state?.revenuePendingOrders?.revenue_pending?.map(
            (order) =>
              order.id === payload.orderId
                ? {...order, remarks: payload.remark}
                : order,
          ),
        },
      };

    case ADD_DELTA_ORDER_REMARK_FAILED:
      return {...state, addingRemark: false, addRemarkError: payload?.error};

    case TOGGLE_BFA_MAPPING_ADD_FORM:
      return {...state, showBFAMappingAddForm: !state.showBFAMappingAddForm};

    case SET_BFA_MAPPING_SEARCH:
      return {...state, bfaMappingSearch: payload};

    case FETCH_BFA_MAPPING_DATA:
      return {...state, isFetchingBFAMapping: true};

    case FETCH_BFA_MAPPING_DATA_COMPLETED:
      return {
        ...state,
        isFetchingBFAMapping: false,
        bfaMappingData: payload,
      };

    case FETCH_BFA_MAPPING_DATA_FAILED:
      return {...state, isFetchingBFAMapping: false};

    case SAVE_BFA_MAPPING:
      return {...state, isSavingBFAMapping: true};

    case SAVE_BFA_MAPPING_COMPLETED: {
      const {data, formData, isEdit, mappingObjId} = payload || {};
      const mappingData = state.bfaMappingData?.mapping_data || {};
      const branch =
        Object.values(state.bfaMappingData?.branches || {}).find(
          (b) => b.id === formData?.branch_id,
        ) || {};
      const user =
        Object.values(state.bfaMappingData?.bfa_user || {}).find(
          (u) => u.id === formData?.user_id,
        ) || {};

      if (isEdit && mappingObjId) {
        const existing = mappingData[mappingObjId]?.basicInfo || {};
        return {
          ...state,
          isSavingBFAMapping: false,
          bfaMappingData: {
            ...state.bfaMappingData,
            mapping_data: {
              ...mappingData,
              [mappingObjId]: {basicInfo: {...existing, user}},
            },
          },
        };
      }
      if (!isEdit && data) {
        const newId = data?.basicInfo?.id ?? data?.id;
        return {
          ...state,
          isSavingBFAMapping: false,
          bfaMappingData: {
            ...state.bfaMappingData,
            count: (state.bfaMappingData?.count || 0) + 1,
            mapping_data: {
              ...mappingData,
              [newId]: {basicInfo: {id: newId, branch, user}},
            },
          },
        };
      }
      return {...state, isSavingBFAMapping: false};
    }

    case SAVE_BFA_MAPPING_FAILED:
      return {...state, isSavingBFAMapping: false};

    default:
      return state;
  }
};
